/**
 * Red dot on the browser-tab icon, mirroring the unread dot on the Messages
 * rail icon. The rail is only visible while the tab is, so this is what tells a
 * founder in another tab that a message landed.
 *
 * The badged icon is drawn once (base favicon → canvas → data URL) and cached,
 * so toggling it afterwards is a single href swap. Every browser API used here
 * is optional: a missing canvas or an icon that won't decode leaves the tab on
 * its plain favicon rather than throwing into a render.
 */

/** `--destructive` from index.css — the same red the in-app dots use. */
const DOT_COLOR = 'hsl(0, 84%, 60%)'
/** Drawn at 64px so the dot still reads on a 2x-DPI 16px tab strip. */
const CANVAS_SIZE = 64
/** Dot radius and the transparent gap punched around it, as fractions of the canvas. */
const DOT_RADIUS = 0.22
const DOT_GAP = 0.06
/** Fallback when index.html's icon link is missing (tests, stripped hosts). */
const DEFAULT_ICON_HREF = '/favicon.png'

/** The app's own favicon, captured before the first badge swaps it out. */
let baseHref: string | null = null
/** Cached badged icon, and the in-flight render that produces it. */
let badgedHref: string | null = null
let badgedRender: Promise<string | null> | null = null
/** Latest requested state — an async render that lands late must not fight it. */
let badgeWanted = false

function getIconLink(): HTMLLinkElement | null {
  if (typeof document === 'undefined') return null

  const existing = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  if (existing) return existing

  const link = document.createElement('link')
  link.rel = 'icon'
  link.href = DEFAULT_ICON_HREF
  document.head.appendChild(link)
  return link
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
    image.src = src
  })
}

async function renderBadgedIcon(src: string): Promise<string | null> {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = CANVAS_SIZE
    canvas.height = CANVAS_SIZE
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    const image = await loadImage(src)
    if (!image) return null
    ctx.drawImage(image, 0, 0, CANVAS_SIZE, CANVAS_SIZE)

    const radius = CANVAS_SIZE * DOT_RADIUS
    const gap = CANVAS_SIZE * DOT_GAP
    const cx = CANVAS_SIZE - radius - gap
    const cy = radius + gap

    // Clear a ring around the dot first so it separates from the logo on both
    // light and dark tab strips — a solid white halo would only work on one.
    ctx.globalCompositeOperation = 'destination-out'
    ctx.beginPath()
    ctx.arc(cx, cy, radius + gap, 0, Math.PI * 2)
    ctx.fill()

    ctx.globalCompositeOperation = 'source-over'
    ctx.fillStyle = DOT_COLOR
    ctx.beginPath()
    ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    ctx.fill()

    return canvas.toDataURL('image/png')
  } catch {
    return null
  }
}

function applyHref(href: string): void {
  // Re-read the link rather than closing over it: TanStack Router's HeadContent
  // portal can replace head nodes between the request and an async render.
  const link = getIconLink()
  if (link && link.getAttribute('href') !== href) link.setAttribute('href', href)
}

/**
 * Show or clear the tab-icon dot. Safe to call on every render — repeat calls
 * with the same state are a no-op, and the first `true` is the only one that
 * pays for a draw.
 */
export function setFaviconBadge(show: boolean): void {
  badgeWanted = show

  const link = getIconLink()
  if (!link) return
  baseHref ??= link.getAttribute('href') || DEFAULT_ICON_HREF

  if (!show) {
    applyHref(baseHref)
    return
  }
  if (badgedHref) {
    applyHref(badgedHref)
    return
  }

  badgedRender ??= renderBadgedIcon(baseHref).then((href) => {
    badgedHref = href
    return href
  })
  void badgedRender.then((href) => {
    // The count may have gone back to zero while the icon was rendering.
    if (href && badgeWanted) applyHref(href)
  })
}
